// @ts-check
// encryption.js — "Encryption" settings section (per active session).
//
// Extracted from settings-panel.js (Stage C of the monolith split). Owns the
// e2e-encryption controls: pair-or-import view, the device list (inline rename +
// remove, like the workspace list), and key backup export/import. Holds the
// section's state (pairing session, device list + cache).
//
// ctx ({ row, rawRow, openExpansions, invalidate, isOpened }) is threaded into
// the handlers that re-render. The host's _wire calls the lifecycle hooks
// exported below on the encryption:ready event and at startup.

import { DevicePairing } from '../../encryption/device-pairing.js';
import { KeyStorage } from '../../encryption/key-storage.js';
import { getEncryption } from '../../encryption/state.js';
import { WorkspaceKeys } from '../../encryption/workspace-keys.js';
import { confirmStatus } from '../../ui/status-pill.js';
import { getSyncHelpers } from '../../storage/session.js';
import { resetEncryptionAndStartFresh } from '../../workspaces/reset-account.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import {
  addCta,
  confirm,
  errorDiv,
  expandHeadingCta,
  makeP,
  prompt,
  removeButton,
  setErrorDiv,
  toast,
} from './_common.js';

// Active pairing session (initiator side). Cleared when the user cancels or
// the wait promise resolves.
/** @type {any} */
let _pairingSession: any = null;
// Set while a user-initiated cancel is in flight so the (async) waitForCompletion
// rejection that follows is swallowed silently instead of surfacing a status
// toast — the user cancelled, it's not a failure.
let _pairingCancelling = false;
// Synchronous single-flight latch: `_pairingSession` is only assigned AFTER the
// `initiatePairing` await, so two activations firing inside that window would
// both pass a `_pairingSession`-only guard and start duplicate server sessions.
// This latches immediately, before the await, and clears in the `finally`.
let _pairingStarting = false;
let _joinError = '';
// Joiner-side SAS, surfaced inline while waiting for the other device to
// approve so the user can compare it against the initiator's modal.
/** @type {string | null} */
let _joinSas: string | null = null;
/** @type {any} null = not yet loaded; [] = loaded empty */
let _devices: any = null;

// Cached local device id so the device list can mark "this device" without
// each render awaiting. Populated once after encryption:ready.
/** @type {any} */
let _myDeviceIdCache: any = null;
let _devicesLoading = false;

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderEncryption(ctx: SettingsSectionCtx) {
  const rows: any[] = [];
  const enc = getEncryption();
  const ID = 'group:encryption';

  // Encryption is unlocked by signing in (it rides the sync session). Until
  // there's a usable encryption state, the section is hidden entirely — it only
  // appears once you've signed in (and there's a key to manage / pair).
  const available = !!enc && (enc.active || enc.needsKey);
  if (!available) return rows;

  // Status text for the states that DON'T render a CTA. active+key and needs-key
  // show a CTA in the value slot instead (see `value:` below), so only PRF
  // mismatch's "Passkey mismatch" and the inert "On" are ever displayed.
  const value = enc.prfMismatch ? 'Passkey mismatch' : 'On';

  // The heading's right-hand value is a clickable CTA in the two actionable
  // states, replacing static status text:
  //   • active+key → "Pair device" — expands the section AND starts the pairing
  //     ceremony;
  //   • needs-key  → "Decrypt" — expands the section to reveal the pair-or-import
  //     form so the user can decrypt this device's notes (was the static
  //     "Needs key" text, which gave no obvious next step).
  // Other states (PRF mismatch, inert) keep their status text. The state is in
  // the reconcile key so the control remounts cleanly across a transition.
  const isOn = !!(enc.active && enc.key);
  const needsDecrypt = !!(enc.needsKey && !enc.key && !enc.prfMismatch);
  rows.push(
    ctx.row({
      id: ID,
      key: `group:encryption:${isOn ? 'pair' : needsDecrypt ? 'decrypt' : 'status'}`,
      label: 'Encryption',
      value: isOn || needsDecrypt ? undefined : value,
      control: isOn
        ? () => buildPairHeadingCta(ctx, ID)
        : needsDecrypt
          ? () => buildDecryptHeadingCta(ctx, ID)
          : undefined,
      expandable: true,
      cls: 'settings-row-group',
      searchText: `encryption end to end e2e pair new device decrypt ${value}`,
    })
  );
  if (!ctx.openExpansions.has(ID)) return rows;

  if (enc.needsKey && !enc.key) {
    // Pair-or-import view. The form is built once and retained (key stable while
    // in this branch), so the pairing-code input keeps focus + value across the
    // _joinError / _joinSas re-renders; `updateJoinForm` patches the error +
    // SAS block, and join.disabled is managed imperatively on the retained node.
    rows.push(
      ctx.rawRow(() => buildJoinForm(ctx, enc), {
        key: 'enc-join',
        update: updateJoinForm,
        indent: true,
        searchText: 'pair device import key backup sas',
      })
    );
    // Last-resort recovery for a user with NO way back to the key (no other
    // device, no backup, no passkey here): reset encryption and start fresh.
    rows.push(
      ctx.rawRow(() => buildResetRow(), {
        key: 'enc-reset',
        indent: true,
        searchText: 'lost key reset encryption start over forgot delete account data',
      })
    );
    return rows;
  }

  // enc.active + enc.key — the full controls, in the Workspaces design
  // language: a flat list under the expanded group (no nested fold-squiggle).
  // The devices are listed directly under a subheading — the analogue of the
  // workspace list — followed by a full-width "＋ Pair new device" primary CTA,
  // then the key-backup and passkey rows.

  // Devices roster — loaded lazily once the section is open, listed directly
  // under a plain subheading (no intermediate "Devices: N" fold or its nested
  // accent line). Mirrors how Workspaces lists workspace rows.
  ensureDevicesLoaded(ctx, enc.userId as any);
  rows.push(
    ctx.rawRow(() => makeP('Devices'), {
      key: 'enc-devices-head',
      indent: true,
      cls: 'settings-row-subhead',
      searchText: 'devices paired',
    })
  );
  if (_devices === null) {
    rows.push(
      ctx.rawRow(() => makeP('Loading…'), {
        key: 'enc-dev-loading',
        indent: true,
        cls: 'settings-row-muted',
      })
    );
  } else if (_devices.length === 0) {
    rows.push(
      ctx.rawRow(() => makeP('No devices yet.'), {
        key: 'enc-dev-empty',
        indent: true,
        cls: 'settings-row-muted',
      })
    );
  } else {
    for (const d of _devices) {
      const isThis = _myDeviceIdCache && d.device_id === _myDeviceIdCache;
      const displayName = d.device_name || d.label || d.device_id || 'Device';
      // Same chrome as a workspace row: an inline-editable name (the current
      // device is bold via `active`, not labelled "(Current)") with a trailing
      // "×" remove. Stable key (no name) so an inline rename patches the label
      // in place instead of remounting and dropping focus mid-edit.
      rows.push(
        ctx.row({
          key: `enc-device:${d.device_id}`,
          label: displayName,
          cls: 'settings-row-workspace',
          indent: true,
          expandable: false,
          active: !!isThis,
          control: () => buildDeviceControls(ctx, enc, d, !!isThis),
          editable: {
            onCommit: (name) => commitDeviceRename(ctx, enc, d, displayName, name),
            ariaLabel: 'Device name',
          },
          searchText: `device ${displayName} ${d.device_id}`,
        })
      );
    }
  }

  // ＋ Pair new device — full-width primary CTA (same styling as the Workspaces
  // "＋ New workspace"). While a pairing session is live the button reads
  // "Pairing…" and is inert; the QR/code panel (with its own Cancel) renders
  // directly below it.
  rows.push(
    ctx.rawRow(
      () =>
        addCta(
          _pairingSession ? `Pairing… ${_pairingSession.code}` : '＋ Pair new device',
          () => startPairing(ctx, enc),
          { disabled: !!_pairingSession }
        ),
      {
        key: `enc-pair-cta:${_pairingSession ? 'active' : 'idle'}`,
        indent: true,
        searchText: 'pair device new add',
      }
    )
  );
  if (_pairingSession) rows.push(...renderPairingPanel(ctx));

  // Key backup — a label-less row of "Export Key" / "Import Key" buttons that
  // together span the row width (same width as the Pair button above).
  rows.push(
    ctx.rawRow(() => buildBackupButtons(enc), {
      key: 'enc:backup',
      indent: true,
      searchText: 'export import key backup encrypted file',
    })
  );

  return rows;
}

// Active state: "Pair device" — expands the section AND kicks off the pairing
// ceremony (fire-and-forget: `before` reads encryption state fresh, so a retained
// control never pairs with a stale key).
function buildPairHeadingCta(ctx: SettingsSectionCtx, ID: string): HTMLElement {
  return expandHeadingCta(ctx, ID, 'Pair device', {
    title: 'Pair a new device',
    before: () => {
      const enc = getEncryption();
      if (enc?.active && enc?.key) startPairing(ctx, enc);
    },
  });
}

// Needs-key state: "Decrypt" — only opens the pair-or-import form (replaces the
// old static "Needs key" text, which gave no next step). No follow-up action:
// the user may want to import a backup rather than pair.
function buildDecryptHeadingCta(ctx: SettingsSectionCtx, ID: string): HTMLElement {
  return expandHeadingCta(ctx, ID, 'Decrypt', {
    title: 'Pair this device or import a key backup to decrypt your notes',
  });
}

// The "Export Key" / "Import Key" buttons (built once + retained). Rendered as a
// label-less row — the button text says what they do, so there's no "Key backup"
// caption. The two buttons share the row width evenly so they together span the
// same width as the full-width "Pair new device" button above.
function buildBackupButtons(enc: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-backup-buttons';
  const exp = document.createElement('button');
  exp.type = 'button';
  exp.className = 'btn';
  exp.textContent = 'Export Key';
  exp.addEventListener('click', () => exportKeyBackup(enc));
  const imp = document.createElement('button');
  imp.type = 'button';
  imp.className = 'btn';
  imp.textContent = 'Import Key';
  imp.addEventListener('click', () => importKeyBackup(enc.userId as any));
  wrap.append(exp, imp);
  return wrap;
}

/**
 * @param {any} ctx
 * @param {any} userId
 * @returns {Promise<void>}
 */
async function ensureDevicesLoaded(ctx: any, userId: any) {
  if (_devices !== null || _devicesLoading || !userId) return;
  if (!DevicePairing?.listDevices) return;
  _devicesLoading = true;
  try {
    _devices = await DevicePairing.listDevices(userId);
  } catch (e: any) {
    console.warn('[settings] listDevices failed:', e);
    _devices = [];
  }
  _devicesLoading = false;
  if (ctx.isOpened()) ctx.invalidate();
}

/**
 * @param {any} ctx
 * @param {any} userId
 * @param {any} deviceId
 * @returns {Promise<void>}
 */
async function removeDevice(ctx: any, userId: any, deviceId: any) {
  if (
    !(await confirm({
      title: 'Remove device?',
      message: 'That device will be signed out the next time it tries to sync.',
      confirmLabel: 'Remove',
      danger: true,
    }))
  )
    return;
  try {
    await DevicePairing.removeDevice(userId, deviceId);
    _devices = null;
    ensureDevicesLoaded(ctx, userId);
  } catch (e: any) {
    console.warn('[settings] removeDevice failed:', e);
  }
}

/** Group a 6-digit SAS as `123-456` (real hyphen — copyable, screenreader-ok). */
function _formatSas(sas: string): string {
  return sas.length === 6 ? `${sas.slice(0, 3)}-${sas.slice(3)}` : sas;
}

/**
 * @param {any} ctx
 * @param {any} enc - the live encryption state.
 * @returns {Promise<void>}
 */
async function startPairing(ctx: any, enc: any) {
  if (_pairingSession || _pairingStarting) return;
  _pairingCancelling = false;
  _pairingStarting = true;
  try {
    _pairingSession = await DevicePairing.initiatePairing(enc.key, enc.userId as any);
    ctx.invalidate();
    // The gate: when a device joins, show a BLOCKING approval modal with the SAS
    // + device name. The master key is not wrapped/uploaded until this resolves
    // true. The modal always settles (Approve/Reject/Escape→false), and being
    // blocking it covers the panel so the in-panel Cancel can't deadlock the
    // `await confirm()` in waitForCompletion.
    const confirmPairing = (info: { sas: string; joinerDeviceName: string | null }) =>
      confirmStatus(
        `Approve “${info.joinerDeviceName || 'this device'}”? Only if this code matches the new device: ${_formatSas(info.sas)}`,
        { kind: 'warning', confirmLabel: 'Approve', cancelLabel: 'Reject' }
      );
    _pairingSession.waitForCompletion(confirmPairing).then(
      () => {
        _pairingSession = null;
        toast('Device paired.', 'info');
        ctx.invalidate();
      },
      (/** @type {any} */ e: any) => {
        _pairingSession = null;
        // A user-initiated cancel isn't a failure — swallow it silently. Any
        // genuine end (expired / rejected / aborted) is shown as a status toast
        // rather than a persistent inline error row.
        if (_pairingCancelling) _pairingCancelling = false;
        else toast(e?.message || 'Pairing ended.', 'warning');
        ctx.invalidate();
      }
    );
  } catch (e: any) {
    toast(e?.message || 'Could not start pairing.', 'warning');
    ctx.invalidate();
  } finally {
    _pairingStarting = false;
  }
}

/**
 * @param {any} ctx
 * @returns {any[]} the pairing-panel rows.
 */
function renderPairingPanel(ctx: any) {
  // Code/QR are fixed for the pairing session's lifetime, so the panel is built
  // once and retained (unmounted when _pairingSession clears) — no `update`.
  return [
    ctx.rawRow(() => buildPairingPanel(ctx), {
      key: 'enc-pair-panel',
      indent: true,
      searchText: 'pair device qr code',
    }),
  ];
}

function buildPairingPanel(ctx: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-pair-panel';
  const code = document.createElement('div');
  code.className = 'settings-pair-code';
  // Hyphenate for readability (ABCD-WXYZ).
  const raw = _pairingSession.code || '';
  code.textContent =
    raw.length >= 6 ? `${raw.slice(0, raw.length / 2)}-${raw.slice(raw.length / 2)}` : raw;
  const intro = document.createElement('p');
  intro.className = 'settings-helptext';
  intro.textContent = 'On the new device, open Settings → Encryption → Pair and enter this code.';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn';
  cancel.textContent = 'Cancel pairing';
  cancel.addEventListener('click', () => {
    // Flag the in-flight cancel so the waitForCompletion rejection it triggers
    // is swallowed (no error toast) — the user chose to stop.
    _pairingCancelling = true;
    try {
      _pairingSession?.cancel();
    } finally {
      _pairingSession = null;
      ctx.invalidate();
    }
  });
  if (_pairingSession.qrDataUrl) {
    const img = document.createElement('img');
    img.src = _pairingSession.qrDataUrl;
    img.alt = 'Pairing QR code';
    img.className = 'settings-pair-qr';
    wrap.appendChild(img);
  }
  wrap.appendChild(code);
  wrap.appendChild(intro);
  wrap.appendChild(cancel);
  return wrap;
}

// Last-resort recovery: the user has lost the key with no device/backup/passkey
// to bring it back. Offer a destructive "reset & start over" that wipes the
// undecryptable data and re-keys the account. Behind a typed (DELETE) confirm.
function buildResetRow(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-reset-encryption';
  wrap.appendChild(
    makeP(
      'No other device, backup, or passkey? You can reset encryption and start over. ' +
        'This permanently deletes the notes in this account — they were encrypted with the ' +
        'key you lost and cannot be recovered.'
    )
  );
  const btn = document.createElement('button');
  btn.className = 'btn btn--danger';
  btn.type = 'button';
  btn.textContent = 'Reset encryption & start over';
  btn.addEventListener('click', async () => {
    const typed = await prompt({
      title: 'Reset encryption — permanent',
      message:
        'This DELETES every note in this account (encrypted under the lost key — unrecoverable) ' +
        'and re-keys you with a fresh key on the same account. Shared workspaces you can no longer ' +
        'decrypt will be left. Type DELETE to confirm.',
      confirmLabel: 'Reset & start over',
      placeholder: 'DELETE',
    });
    if (typed == null) return; // cancelled
    if (typed.trim() !== 'DELETE') {
      toast('Reset cancelled — confirmation text did not match.', 'warning');
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Resetting…';
    try {
      await resetEncryptionAndStartFresh(); // reloads on success
    } catch (e: any) {
      btn.disabled = false;
      btn.textContent = 'Reset encryption & start over';
      toast(e?.message || 'Reset failed.', 'warning');
    }
  });
  wrap.appendChild(btn);
  return wrap;
}

// The pairing-code input + join/import buttons + error + SAS block. Built once
// (retained while the joiner branch is active); updateJoinForm patches state.
function buildJoinForm(ctx: SettingsSectionCtx, enc: any): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'settings-form';
  // PRF mismatch — the WebAuthn ceremony succeeded but the derived bytes don't
  // decrypt the wrapped key; a fresh attempt with the same authenticator can't
  // recover it, so spell out "pair from another device / import a backup".
  if (enc.prfMismatch) {
    const banner = document.createElement('p');
    banner.className = 'settings-helptext settings-helptext-error';
    banner.textContent =
      'Passkey unlock didn’t produce the right key bytes for this account. ' +
      'Re-pair from another signed-in device, or import a key backup below.';
    wrap.appendChild(banner);
  }
  const intro = document.createElement('p');
  intro.className = 'settings-helptext';
  intro.textContent = 'Pair this device from an existing one, or import a key backup.';
  wrap.appendChild(intro);

  const code = document.createElement('input');
  code.type = 'text';
  code.placeholder = 'XXXX-XXXX';
  code.maxLength = 9;
  code.className = 'settings-mini-input';
  code.style.fontFamily = 'var(--mono, monospace)';
  const join = document.createElement('button');
  join.type = 'button';
  join.className = 'btn btn--primary';
  join.textContent = 'Pair';
  const submitJoin = async () => {
    const raw = (code.value || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
    if (raw.length < 6) {
      _joinError = 'Enter the pairing code from the other device.';
      ctx.invalidate();
      return;
    }
    join.disabled = true;
    _joinError = '';
    _joinSas = null;
    ctx.invalidate();
    try {
      const keyBytes = await DevicePairing.joinPairing(raw, enc.userId as any, (sas) => {
        // Surface the SAS inline so the user can compare it against the digits
        // on the initiating device before they approve there.
        _joinSas = sas;
        if (ctx.isOpened?.()) ctx.invalidate();
      });
      await _applyNewMasterKey(enc.userId, keyBytes, 'Paired');
    } catch (e: any) {
      _joinError = e?.message || 'Pairing failed.';
      _joinSas = null;
      join.disabled = false;
    }
    ctx.invalidate();
  };
  join.addEventListener('click', submitJoin);
  code.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitJoin();
    }
  });
  wrap.append(code, join);

  const importBtn = document.createElement('button');
  importBtn.type = 'button';
  importBtn.className = 'btn';
  importBtn.textContent = 'Import key backup';
  importBtn.addEventListener('click', () => importKeyBackup(enc.userId as any));
  wrap.appendChild(importBtn);

  // Always-present (hidden when empty) so updateJoinForm can toggle them without
  // remounting and tearing out the focused code input.
  wrap.appendChild(errorDiv(_joinError));

  const sasWrap = document.createElement('div');
  sasWrap.className = 'settings-sas-gate';
  sasWrap.hidden = !_joinSas;
  const sasEl = document.createElement('div');
  sasEl.className = 'settings-sas-value';
  sasEl.textContent = _joinSas ? _formatSas(_joinSas) : '';
  const note = document.createElement('p');
  note.className = 'settings-sas-warn';
  note.textContent = 'Confirm these digits match the other device, then approve there.';
  sasWrap.append(sasEl, note);
  wrap.appendChild(sasWrap);
  return wrap;
}

function updateJoinForm(el: HTMLElement): void {
  setErrorDiv(el.querySelector('.settings-error'), _joinError);
  const sasWrap = el.querySelector('.settings-sas-gate') as HTMLElement | null;
  const sasEl = el.querySelector('.settings-sas-value') as HTMLElement | null;
  if (sasEl) sasEl.textContent = _joinSas ? _formatSas(_joinSas) : '';
  if (sasWrap) sasWrap.hidden = !_joinSas;
}

// The trailing control for a device row — just a "×" remove (the inline-edit
// label owns rename). Same chrome as the workspace row's delete button. The
// current device can't be removed here (you'd be unpairing the device you're
// using) — its "×" is disabled, mirroring the active-workspace delete.
function buildDeviceControls(
  ctx: SettingsSectionCtx,
  enc: any,
  d: any,
  isThis: boolean
): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'settings-row-control';
  const name = d.device_name || d.label || 'device';
  const rm = removeButton(
    isThis
      ? {
          ariaLabel: "Can't remove the current device",
          title: "This is the device you're using — it can't be removed here",
          disabled: true,
        }
      : {
          ariaLabel: `Remove ${name}`,
          title: 'Remove device',
          onClick: () => removeDevice(ctx, enc.userId as any, d.device_id),
        }
  );
  wrap.appendChild(rm);
  return wrap;
}

// Inline-rename commit for a device row (mirrors the workspace inline rename):
// persist via DevicePairing.renameDevice, reflect the new name in the cached
// list so the row keeps it without a full reload, then re-render.
async function commitDeviceRename(
  ctx: SettingsSectionCtx,
  enc: any,
  d: any,
  oldName: string,
  raw: string
): Promise<void> {
  const name = (raw || '').trim();
  if (!name || name === oldName) {
    ctx.invalidate(); // restore the displayed label
    return;
  }
  try {
    await DevicePairing?.renameDevice?.(enc.userId as any, d.device_id, name);
    d.device_name = name; // keep the cached entry in sync so the row shows the new name
  } catch (e: any) {
    console.warn('[settings] rename device failed:', e);
  }
  ctx.invalidate();
}

/**
 * @param {any} enc - the live encryption state.
 * @returns {Promise<void>}
 */
async function exportKeyBackup(enc: any) {
  const pass = await prompt({
    title: 'Export key backup',
    message: 'Choose a passphrase. You will need it to import this backup later.',
    placeholder: 'Passphrase',
    password: true,
    confirmLabel: 'Export',
  });
  if (!pass) return;
  try {
    // DevicePairing.exportKeyBackup performs the download itself (canonical name
    // ariadnenotes-key-backup.json) and returns void. Do NOT also pass its return
    // value to downloadFile — that's `undefined`, which writes a second junk file
    // whose entire content is the string "undefined", and importing THAT fails
    // with `JSON.parse("undefined")`. One call, one file.
    await DevicePairing.exportKeyBackup(enc.key, pass);
    toast('Backup downloaded.', 'info');
  } catch (e: any) {
    console.warn('[settings] exportKeyBackup failed:', e);
    toast('Export failed.', 'warning');
  }
}

// After a master key becomes locally available (device pairing OR key-backup
// import), persist it, register the device, publish the static keypair, and
// claim any pending workspace keys — then re-run encryption setup IN PLACE
// (reinitEncryption: re-wrap NoteStorage + refresh the UI) with a reload
// fallback. `verb` is the past-tense word for the toast ("Paired" / "Imported").
/**
 * @param {any} userId @param {Uint8Array} keyBytes @param {string} verb
 * @returns {Promise<void>}
 */
async function _applyNewMasterKey(userId: any, keyBytes: any, verb: string) {
  if (KeyStorage?.saveMasterKey) await KeyStorage.saveMasterKey(keyBytes, userId);
  if (DevicePairing.registerDevice) await DevicePairing.registerDevice(userId);
  if (WorkspaceKeys?.publishStaticKeypair)
    await WorkspaceKeys.publishStaticKeypair(userId, keyBytes);
  if (WorkspaceKeys?.claimPendingFromLocalStorage)
    await WorkspaceKeys.claimPendingFromLocalStorage(userId, keyBytes);
  const reinitEnc = getSyncHelpers()?.reinitEncryption;
  if (typeof reinitEnc === 'function') {
    toast(`${verb}.`, 'info');
    try {
      await reinitEnc();
    } catch (e: any) {
      console.warn(
        `[encryption] in-place reinit after ${verb.toLowerCase()} failed; reloading:`,
        e?.message
      );
      setTimeout(() => location.reload(), 800);
    }
  } else {
    toast(`${verb} — reloading.`, 'info');
    setTimeout(() => location.reload(), 800);
  }
}

/**
 * @param {any} userId
 * @returns {Promise<void>}
 */
async function importKeyBackup(userId: any) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  const removeInput = () => input.remove();
  // A cancelled file dialog never fires 'change'; the 'cancel' event lets us
  // remove the orphaned input (and its listener) instead of leaking it.
  input.addEventListener('cancel', removeInput);
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    removeInput();
    if (!file) return;
    const pass = await prompt({
      title: 'Import key backup',
      message: 'Enter the passphrase used when this backup was created.',
      placeholder: 'Passphrase',
      password: true,
      confirmLabel: 'Import',
    });
    if (!pass) return;
    try {
      const keyBytes = await DevicePairing.importKeyBackup(file, pass);
      await _applyNewMasterKey(userId, keyBytes, 'Imported');
    } catch (e: any) {
      console.warn('[settings] importKeyBackup failed:', e);
      toast(e?.message || 'Import failed.', 'warning');
    }
  });
  input.click();
}

// ── Lifecycle hooks called by the host's _wire ─────────────────────────────

// On encryption:ready — reset the device list (so it reloads for the new
// session) and re-cache the local device id so the first device-list render can
// mark "this device".
/** @returns {void} */
export function handleEncryptionReady() {
  _devices = null;
  _myDeviceIdCache = KeyStorage?.getDeviceId?.() || null;
}

// Populate the device-id cache eagerly if encryption is already ready at
// startup (so the first device-list render can mark "this device").
/** @returns {void} */
export function primeDeviceId() {
  _myDeviceIdCache = KeyStorage?.getDeviceId?.() || null;
}
